import { call, CallEffect, put, PutEffect, select, SelectEffect, debounce, takeLatest } from 'redux-saga/effects';

import { AUTH_SUCCESS } from 'js/actionCreators/authActions';
import { showError } from 'js/actionCreators/uiActions';
import { APP_DATA_KEYS } from 'js/config/consts';
import {
  setLastSynced,
  updateColor,
  addColor,
  removeColor,
  loadColorsSuccess,
  ColorSlot
} from 'js/reducers/brandKitColors';
import { appServices } from 'js/shared/helpers/app-services/AppServices';
import { HTTPError, RootState } from 'js/types';

export const getBrandKitColors = (state: RootState) => state.brandKitColors.colors;

export function* syncColors(): Generator<SelectEffect | CallEffect | PutEffect, void, ColorSlot[]> {
  try {
    const colors = yield select(getBrandKitColors);
    yield call({ fn: appServices.setAppData, context: appServices }, APP_DATA_KEYS.BRAND_KIT_COLORS, colors);
    yield put(setLastSynced(Date.now()));
  } catch (error) {
    yield put(
      showError({
        message: 'Error syncing brand kit colors. Please try again later.',
        description: error instanceof Error ? error.message : undefined
      })
    );
    console.error('Error syncing brand kit colors:', error);
    return;
  }
}

export function* loadColors(): Generator<CallEffect | PutEffect, void, ColorSlot[]> {
  try {
    const colors = yield call({ fn: appServices.getAppData, context: appServices }, APP_DATA_KEYS.BRAND_KIT_COLORS);
    yield put(loadColorsSuccess(colors));
  } catch (error) {
    const httpError = error as HTTPError;
    const statusCode = httpError?.httpStatusCode;

    // User hasn't stored any brand kit colors yet - 404 is expected
    if (statusCode === 404 || statusCode === '404') {
      console.log('Brand kit colors not found (404) - using defaults');
      return;
    }

    console.error('Error loading brand kit colors:', error);
    const errorMessage = error instanceof Error ? String(error.message) : String(error);
    yield put(
      showError({
        message: 'Error loading brand kit colors. Please try again later.',
        description: errorMessage
      })
    );
  }
}

export const COLORS_SYNC_DELAY = 2000;
export default function* brandKitColorsSaga() {
  yield debounce(COLORS_SYNC_DELAY, [updateColor.type, addColor.type, removeColor.type], syncColors);
  yield takeLatest(AUTH_SUCCESS, loadColors);
}
